import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import Modal from '../../ui/Modal';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import {
  formatFinancialRequirement,
  PROJECT_STATUS
} from '../../../constants/projectConstants';
import { getSectorLabel } from '../../../constants/authConstants';
import { validateApprovedAmount } from '../../../utils/projectValidation';

const ProjectApprovalModal = ({ 
  isOpen, 
  onClose, 
  project, 
  onApprove,
  loading = false 
}) => {
  const { isMobile, getResponsiveValue } = useTheme();
  const { getDepartmentLabel } = useDepartments();
  
  const [approvedAmount, setApprovedAmount] = useState('');
  const [errors, setErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'w-full',
    desktop: 'w-auto'
  });

  useEffect(() => {
    if (isOpen && project) {
      setApprovedAmount(project.financialRequirement?.toString() || '');
      setErrors({});
    } else {
      setApprovedAmount('');
      setErrors({});
    }
  }, [isOpen, project]);

  const handleApprovedAmountChange = (e) => {
    const value = e.target.value;
    setApprovedAmount(value);
    
    if (errors.approvedAmount) {
      setErrors(prev => ({ ...prev, approvedAmount: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    const amountError = validateApprovedAmount(approvedAmount, true);
    if (amountError) {
      newErrors.approvedAmount = amountError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateForm()) return;
    
    try {
      setActionLoading(true);
      await onApprove(project.id, PROJECT_STATUS.APPROVED, parseFloat(approvedAmount));
      handleClose();
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    if (!actionLoading) {
      setApprovedAmount('');
      setErrors({});
      onClose();
    }
  };

  if (!project) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Review Project Proposal"
      size="lg"
      maxHeight="large"
    >
      <div className={containerPadding}>
        <div className="space-y-6">
          {errors.general && (
            <div className="bg-error-subtle border border-border-error rounded-xl p-3">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-error text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="pb-4 border-b border-border-primary">
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              {project.projectName}
            </h3>
            <p className="text-sm text-text-secondary">
              Review this project proposal and enter the approved amount
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">DEPARTMENT</h4>
                <p className="text-text-primary">{getDepartmentLabel(project.department)}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">SECTOR</h4>
                <p className="text-text-primary">{getSectorLabel(project.sector)}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">FINANCIAL YEAR</h4>
                <p className="text-text-primary">
                  {project.financialYear ? `FY ${project.financialYear}` : 'Not specified'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">Proposed Estimated Cost (in lakh)</h4>
                <p className="text-text-primary font-semibold text-lg">
                  {formatFinancialRequirement(project.financialRequirement)}
                </p>
              </div>

              <div>
                <Input
                  label="Approved Estimated Cost (in lakh)"
                  type="number"
                  value={approvedAmount}
                  onChange={handleApprovedAmountChange}
                  placeholder="Enter approved amount in lakhs"
                  error={errors.approvedAmount}
                  required
                  disabled={actionLoading}
                  icon={<span className="text-gray-400 font-semibold">₹</span>}
                />
                <p className="text-xs text-text-tertiary mt-1">
                  You can approve a different amount than the proposed amount
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-text-secondary mb-3">PROJECT DESCRIPTION</h4>
            <div className="bg-surface-secondary rounded-lg p-4 max-h-32 overflow-y-auto">
              <p className="text-text-primary leading-relaxed whitespace-pre-line text-sm">
                {project.brief}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border-primary justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={actionLoading}
              className={buttonSize}
            >
              Cancel
            </Button>
            
            <Button
              variant="primary"
              onClick={handleApprove}
              disabled={actionLoading || !approvedAmount.trim()}
              loading={actionLoading}
              className={buttonSize}
            >
              Approve Project
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProjectApprovalModal;